import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Brain, Trash2, Loader2, MessageSquare, Tag } from 'lucide-react';
import { API } from '../lib/apiBase';

const Memory = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/me/profile`, { withCredentials: true });
      setProfile(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => { if (user) loadProfile(); }, [user, loadProfile]);

  const handleClear = async () => {
    if (!window.confirm('Tüm hafıza bilgileriniz silinecek. Emin misiniz?')) return;
    setClearing(true);
    try {
      await axios.delete(`${API}/me/profile`, { withCredentials: true });
      await loadProfile();
    } finally {
      setClearing(false);
    }
  };

  if (authLoading || !user) return null;

  const brands = Object.entries(profile?.brands || {}).sort((a, b) => b[1] - a[1]);
  const models = Object.entries(profile?.models || {}).sort((a, b) => b[1] - a[1]);
  const questions = (profile?.recent_questions || []).slice().reverse();
  const isEmpty = brands.length === 0 && models.length === 0 && questions.length === 0;

  return (
    <div className="min-h-screen bg-background" data-testid="memory-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <Brain className="w-3.5 h-3.5" /> KİŞİSEL HAFIZA
            </p>
            <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tighter" data-testid="memory-title">
              Volt Usta&apos;nın Sizinle İlgili Bildikleri
            </h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">
              Sohbetleriniz boyunca sorduğunuz markalar, modeller ve sorular Volt Usta&apos;nın
              size daha kişisel tavsiyeler verebilmesi için hatırlanır. İstediğiniz an silebilirsiniz.
            </p>
          </div>
          {!isEmpty && (
            <Button onClick={handleClear} variant="destructive" disabled={clearing}
              className="rounded-sm flex-shrink-0" data-testid="clear-memory-button">
              {clearing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Hafızayı Temizle
            </Button>
          )}
        </div>

        {loading ? (
          <p className="text-muted-foreground">Yükleniyor...</p>
        ) : isEmpty ? (
          <div className="border border-border bg-card p-8 text-center" data-testid="memory-empty">
            <Brain className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              Henüz hafıza yok. Sohbet ettikçe ilgilendiğiniz araçlar burada birikecek.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-border bg-card p-6" data-testid="memory-brands-card">
              <h2 className="text-sm uppercase tracking-wider font-bold mb-4 flex items-center gap-2">
                <Tag className="w-4 h-4 text-primary" />
                İlgilendiğiniz Markalar
              </h2>
              {brands.length === 0 ? (
                <p className="text-sm text-muted-foreground">Yok</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {brands.map(([b, count]) => (
                    <span key={b} className="text-sm font-mono px-3 py-1 border border-primary/30 bg-primary/5 text-foreground"
                      data-testid={`brand-${b}`}>
                      {b} <span className="text-muted-foreground">×{count}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-border bg-card p-6" data-testid="memory-models-card">
              <h2 className="text-sm uppercase tracking-wider font-bold mb-4 flex items-center gap-2">
                <Tag className="w-4 h-4 text-primary" />
                İlgilendiğiniz Modeller
              </h2>
              {models.length === 0 ? (
                <p className="text-sm text-muted-foreground">Yok</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {models.map(([m, count]) => (
                    <span key={m} className="text-sm font-mono px-3 py-1 border border-border bg-secondary text-foreground"
                      data-testid={`model-${m.replace(/\s+/g, '-')}`}>
                      {m} <span className="text-muted-foreground">×{count}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-border bg-card p-6 md:col-span-2" data-testid="memory-questions-card">
              <h2 className="text-sm uppercase tracking-wider font-bold mb-4 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-primary" />
                Son Sorularınız
              </h2>
              {questions.length === 0 ? (
                <p className="text-sm text-muted-foreground">Yok</p>
              ) : (
                <ul className="space-y-2">
                  {questions.map((q, idx) => (
                    <li key={`${q.at}-${idx}`} className="text-sm border-l-2 border-primary/30 pl-3 py-1"
                      data-testid={`memory-question-${idx}`}>
                      <p className="text-foreground">{q.text}</p>
                      <p className="text-xs text-muted-foreground font-mono mt-0.5">
                        {q.at ? new Date(q.at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Memory;
